import { memo, useEffect, useMemo, useRef, useState } from "react"

// ───────────────────────────────────────────────
// Utils
// ───────────────────────────────────────────────
const clamp01 = (n) => Math.max(0, Math.min(1, n))
const slugify = (s) => (s || "").toString().trim().toLowerCase()

// Mapea zona → color. AJUSTA a tus slugs/nombres reales.
const COLOR_BY_SLUG = {
  azul: "BlueAdsl",
  oro: "GoldenAdsl",
  moctezuma: "GoldenAdsl",
  nissan: "WhiteAdsl",
  mobil: "GoldenAdsl",
  tecate: "RedAdsl",
  "laboratorio tequis": "RedAdsl",
  "seguros el potosi": "GoldenAdsl",
  "red cola": "RedAdsl",
  canels: "BlueAdsl",
  "lounge ron potosi": "GoldenAdsl",
  lucho: "BlueAdsl",
  "team lucho": "BlueAdsl",
  "1": "BlueAdsl",
  "zona 1": "BlueAdsl",
  tunita: "RedAdsl",
  "team tunita": "RedAdsl",
  "2": "RedAdsl",
  "zona 2": "RedAdsl",
}

// Mapea zona → GIF (Lucho vs Tunita)
const GIF_BY_SLUG = {
  lucho: "/img/games/balloons/female/august-30-2026/lucho.gif",
  "team lucho": "/img/games/balloons/female/august-30-2026/lucho.gif",
  "1": "/img/games/balloons/female/august-30-2026/lucho.gif",
  "zona 1": "/img/games/balloons/female/august-30-2026/lucho.gif",
  tunita: "/img/games/balloons/female/august-30-2026/tunita.gif",
  "team tunita": "/img/games/balloons/female/august-30-2026/tunita.gif",
  "2": "/img/games/balloons/female/august-30-2026/tunita.gif",
  "zona 2": "/img/games/balloons/female/august-30-2026/tunita.gif",
}

const getZoneGif = (zoneNameOrSlug) =>
  GIF_BY_SLUG[slugify(zoneNameOrSlug)] || "/img/games/balloons/inflate-balloons.gif"

const getColorToken = (zoneNameOrSlug) =>
  COLOR_BY_SLUG[slugify(zoneNameOrSlug)] || "RedAdsl"

// Clases BEM por bloque
const colorClassFor = (scope /* 'pump' | 'box' | 'float' */, zone) => {
  const token = getColorToken(zone)
  if (scope === "pump")  return `Balloons__Screen__AirPump__Balloons__Balloon--${token}`
  if (scope === "box")   return `Balloons__Screen__Box__Balloons__Slot__Balloon--${token}`
  return `Balloons__Screen__Players__Player__FloatBallon__FilledBalloon__Balloon--${token}`
}

// ───────────────────────────────────────────────
// Estado visual por zona
// ───────────────────────────────────────────────
function initZoneState() {
  return {
    tapsAccum: 0,              // remainder actual (0..target-1)
    filledBalloons: 0,         // contador total visibles
    boxSlots: [
      { visible: false, pulseKey: 0 },
      { visible: false, pulseKey: 0 },
      { visible: false, pulseKey: 0 },
    ],
    nextBoxIndex: 0,           // 0..2
    floatingQueue: [],         // [{ id }]
    pumpBurstUntil: 0,         // timestamp ms para “llenado” perceptible
  }
}

export default function Balloons({ state, ui, isActiveWomensMode }) {
  // Snapshot autoritativo que manda el padre (Screen)
  const match = state ?? {
    matchId: null,
    status: "Waiting",
    target: 0,
    zones: [],
    winners: null
  }

  const matchId = match.matchId
  const target  = match.target || 0
  const status  = match.status || "Waiting"
  const winners = match.winners
  const zonesSnap = Array.isArray(match.zones) ? match.zones : []

  // Diccionario visual: slug -> ZoneVisualState
  const [zonesVisual, setZonesVisual] = useState({})
  const idSeq = useRef(1)                           // IDs para globos flotantes
  const lastServerCompletedRef = useRef(new Map())  // slug -> último completed del servidor

  // RESET COMPLETO cuando cambia de partida
  useEffect(() => {
    if (!matchId) return
    setZonesVisual({})
    lastServerCompletedRef.current = new Map()
  }, [matchId])

  // Ticking ligero para apagar el "burst" (inflado perceptible) sin depender de nuevos snapshots
  const [, forceUiTick] = useState(0)
  const scheduleUiTick = (ms) => {
    setTimeout(() => forceUiTick((x) => x + 1), ms)
  }

  // DEPURACIÓN: imprime target y taps por zona (colocado aquí)
  useEffect(() => {
    // Revisa en consola: target, taps y completed de cada zona
    // Si ves target=10 siempre, el origen viene del servidor (revisa handlers del padre).
    // eslint-disable-next-line no-console
    console.log("[Balloons] target:", target, "zones:", zonesSnap.map(z => ({
      zone: z.zone,
      taps: z.currentBalloonTaps,
      completed: z.completedBalloons
    })))
  }, [zonesSnap, target])

  // Helpers inmutables
  const ensureZone = (slug, dict) => {
    if (!dict[slug]) dict[slug] = initZoneState()
  }

  const addCompletedToZone = (zone, howMany) => {
    if (howMany <= 0) return { ...zone }

    // 1) contador
    let filledBalloons = zone.filledBalloons + howMany

    // 2) flotantes (inmutable)
    let floatingQueue = zone.floatingQueue
    for (let i = 0; i < howMany; i++) {
      floatingQueue = [...floatingQueue, { id: idSeq.current++ }]
    }

    // 3) caja (3 slots carrusel), inmutable
    let boxSlots = zone.boxSlots.map(s => ({ ...s }))
    let nextBoxIndex = zone.nextBoxIndex
    for (let i = 0; i < howMany; i++) {
      const idx = nextBoxIndex % 3
      boxSlots[idx].visible = true
      boxSlots[idx].pulseKey = (boxSlots[idx].pulseKey ?? 0) + 1
      nextBoxIndex = (nextBoxIndex + 1) % 3
    }

    // 4) “burst” de inflado perceptible (180ms a full)
    const pumpBurstUntil = Date.now() + 180
    scheduleUiTick(190)

    return {
      ...zone,
      filledBalloons,
      floatingQueue,
      boxSlots,
      nextBoxIndex,
      pumpBurstUntil,
    }
  }

  // Reconciliar visual cuando cambie el snapshot o el target
  useEffect(() => {
    if (!zonesSnap.length) return

    setZonesVisual(prev => {
      let next = { ...prev }

      for (const z of zonesSnap) {
        const slug = slugify(z.zone)
        ensureZone(slug, next)
        const current = next[slug]

        const serverCompleted = Math.max(0, z.completedBalloons || 0)
        const serverRemainder = Math.max(0, z.currentBalloonTaps || 0)

        // DIF basado en el último valor del SERVIDOR (idempotente)
        const lastSeen = lastServerCompletedRef.current.get(slug) ?? 0
        const completedDiff = serverCompleted - lastSeen

        let updated = current

        if (completedDiff > 0) {
          updated = addCompletedToZone(updated, completedDiff)
        }

        // Actualiza marcador del servidor (MUY IMPORTANTE para evitar duplicados)
        lastServerCompletedRef.current.set(slug, serverCompleted)

        // Inflado proporcional por remainder (si target==10 y server manda 10 de golpe, veremos el "burst")
        let tapsAccum = 0
        if (target > 0) {
          const capped = Math.min(serverRemainder, target - 1)
          tapsAccum = Math.max(0, capped)
        }
        if (updated.tapsAccum !== tapsAccum) {
          updated = { ...updated, tapsAccum }
        }

        // Sincroniza contador visual con el servidor (por seguridad)
        if (updated.filledBalloons < serverCompleted) {
          updated = { ...updated, filledBalloons: serverCompleted }
        }

        if (updated !== current) {
          next = { ...next, [slug]: updated }
        }
      }

      // Limpia zonas que ya no vienen en el snapshot
      const alive = new Set(zonesSnap.map(z => slugify(z.zone)))
      for (const k of Object.keys(next)) {
        if (!alive.has(k)) {
          const copy = { ...next }
          delete copy[k]
          next = copy
          lastServerCompletedRef.current.delete(k)
        }
      }

      return next
    })
  }, [zonesSnap, target])

  // Remueve flotante al terminar animación (inmutable)
  const handleFloatingEnd = (slug, id) => {
    setZonesVisual(prev => {
      const zone = prev[slug]
      if (!zone) return prev
      return {
        ...prev,
        [slug]: {
          ...zone,
          floatingQueue: zone.floatingQueue.filter(f => f.id !== id)
        }
      }
    })
  }

  const showFinishedBanner = status === "Finished" && winners && winners.winnerType !== "none"

  return (
    <div className="Balloons__Screen">

      {/* ─────────────────────────────────────────────── */}
      {/* FONDO (BACKGROUND) CONDICIONAL */}
      {/* ─────────────────────────────────────────────── */}
      <div className="Balloons__Screen__Brands--RojitoDrinks__Background">
        {isActiveWomensMode ? (
            <img 
                //src='/img/page/backgrounds/background.png'
                src='/img/games/balloons/background.jpeg'
                className='Balloons__Screen__Brands--RojitoDrinks__Background__Image'
                alt="Fondo Femenil"
            />
        ) : isActiveWomensMode === false && (
            <img 
                //src='/img/page/backgrounds/background.png'
                src='/img/games/balloons/background.jpeg'
                className='Balloons__Screen__Brands--RojitoDrinks__Background__Image'
                alt="Fondo Varonil"
            />
        )}
      </div>

      {/* <p className="Balloons__Screen__Title">Únete a la dinámica ingresando a WWW.ADSL.MX</p> */}
      <p className="Balloons__Screen__Title"></p>

      {showFinishedBanner && <WinnersBanner winners={winners} />}

      {(ui?.uiStatus === "Countdown" || ui?.uiStatus === "Running" || status === "Running") && (
        <div className="Balloons__Screen__Players">
          {zonesSnap.map((z) => {
            const slug = slugify(z.zone)
            const v = zonesVisual[slug] || initZoneState()

            // Si hubo "burst", mostramos 1.0 por ~180ms; si no, usamos remainder/target
            const progressBase = target > 0 ? clamp01(v.tapsAccum / target) : 0
            const now = Date.now()
            const effectiveProgress = (v.pumpBurstUntil > now) ? 1 : progressBase

            // Color (bomba, caja, flotantes)
            const pumpColorClass  = colorClassFor("pump",  z.zone)
            const boxColorClass   = colorClassFor("box",   z.zone)
            const floatColorClass = colorClassFor("float", z.zone)

            return (
              <PlayerColumn
                key={slug}
                slug={slug}
                zoneName={z.zone}
                counter={v.filledBalloons}
                progress={effectiveProgress}
                boxSlots={v.boxSlots}
                floatingQueue={v.floatingQueue}
                pumpColorClass={pumpColorClass}
                boxColorClass={boxColorClass}
                floatColorClass={floatColorClass}
                onFloatingEnd={handleFloatingEnd}
              />
            )
          })}
        </div>
      )}

      <p className="Balloons__Screen__Footer FooterCredits">Hecho con ♥ por Interaktia | 2026</p>
    </div>
  )
}

// ───────────────────────────────────────────────
// Presentación
// ───────────────────────────────────────────────
function WinnersBanner({ winners }) {
  const t = winners?.winnerType
  if (t === "single") {
    const z = winners?.winners?.[0] ?? ""
    return (
      <div className="Balloons__Screen__Winners">
        <p className='Balloons__Screen__Winners__Text--Single'>La zona ganadora es...</p>
        <p className='Balloons__Screen__Winners__Text--Single'>¡{z.toUpperCase()}!</p>
      </div>
    )
  }
  if (t === "tie") {
    const list = winners?.winners?.toUpperCase().join(", ") ?? ""
    return (
      <div className="Balloons__Screen__Winners">
        <p className='Balloons__Screen__Winners__Text--Tie'>Ha habido un empate entre las zonas</p>
        <p className='Balloons__Screen__Winners__Text--Tie'>{list}</p>
      </div>
    )
  }
  if (t === "all-tie") {
    return (
      <div className="Balloons__Screen__Winners">
        <p className='Balloons__Screen__Winners__Text--AllTie'>¡Han empatado todas las zonas!</p>
      </div>
    )
  }
  return null
}

function PlayerColumn({
  slug,
  zoneName,
  counter,
  progress,
  boxSlots,
  floatingQueue,
  pumpColorClass,
  boxColorClass,
  floatColorClass,
  onFloatingEnd
}) {
  const gifSrc = getZoneGif(zoneName || slug)

  return (
    <div className="Balloons__Screen__Players__Player">
      <Box zoneName={zoneName} counter={counter} boxSlots={boxSlots} boxColorClass={boxColorClass} />
      <AirPump progress={progress} pumpColorClass={pumpColorClass} gifSrc={gifSrc} zoneName={zoneName} />
      {/* <FloatBalloonsRenderer
        slug={slug}
        floatingQueue={floatingQueue}
        floatColorClass={floatColorClass}
        onEnd={onFloatingEnd}
      /> */}
    </div>
  )
}

function Box({ zoneName, counter, boxSlots, boxColorClass }) {
  return (
    <div className='Balloons__Screen__Box'>
      <div className='Balloons__Screen__Box__Balloons'>
        <div className='Balloons__Screen__Box__Balloons__Slot'>
          {boxSlots.map((s, i) => (
            <div
              key={i + "-" + s.pulseKey}
              className={[
                "Balloons__Screen__Box__Balloons__Slot__Balloon",
                boxColorClass,
                s.visible ? "is-visible" : "is-hidden"
              ].join(" ")}
            />
          ))}
        </div>
      </div>
      <div className='Balloons__Screen__Box__Box' />
      <div className='Balloons__Screen__Box__Counter'>
        <p className='Balloons__Screen__Box__Counter__Text'>{zoneName.toUpperCase()}</p>
        <p className='Balloons__Screen__Box__Counter__Text'>{counter}</p>
      </div>
    </div>
  )
}

function AirPump({ progress, pumpColorClass, gifSrc, zoneName }) {
  // para que React NO re-pinte el nodo cada frame, memoizamos el estilo
  const styleVar = useMemo(() => ({ "--p": Number.isFinite(progress) ? progress : 0 }), [progress])
  return (
    <div className='Balloons__Screen__AirPump'>
      <div className='Balloons__Screen__AirPump__Balloons'>
        <div
          className={["Balloons__Screen__AirPump__Balloons__Balloon", pumpColorClass].join(" ")}
          style={styleVar}
        />
      </div>
      <div className='Balloons__Screen__AirPump__Image'>
        <img src={gifSrc || '/img/games/balloons/inflate-balloons.gif'} alt={zoneName || 'Bomba'} />
      </div>
    </div>
  )
}

// Componente de globo flotante que NO se remonta al re-render del padre
const AnimatedBalloon = memo(function AnimatedBalloon({ className, onEnd }) {
  // No depende de props cambiantes; el nodo permanece y su animación CSS no se reinicia
  return <div className={className} onAnimationEnd={onEnd} />
})

function FloatBalloonsRenderer({ slug, floatingQueue, floatColorClass, onEnd }) {
  return (
    <div className='Balloons__Screen__Players__Player__FloatBallon'>
      <div className='Balloons__Screen__Players__Player__FloatBallon__FilledBalloon'>
        {floatingQueue.map(f => (
          <AnimatedBalloon
            key={f.id}
            className={[
              "Balloons__Screen__Players__Player__FloatBallon__FilledBalloon__Balloon",
              floatColorClass,
              "is-floating"
            ].join(" ")}
            onEnd={() => onEnd?.(slug, f.id)}
          />
        ))}
      </div>
    </div>
  )
}